import { ReactNode, useEffect } from 'react';
import { useSiteSettings, type SiteSettings } from '@/hooks/useSiteSettings';
import { useAuth } from '@/hooks/useAuth';
import NotFound from '@/pages/NotFound';

interface Props {
  children: ReactNode;
  settingsKey: keyof SiteSettings;
}

const ProtectedPage = ({ children, settingsKey }: Props) => {
  const { settings, loading } = useSiteSettings();
  const { isAdmin } = useAuth();

  const isHidden = !loading && !isAdmin && !settings[settingsKey];

  // Inject noindex for hidden pages so crawlers don't index soft-404 content
  useEffect(() => {
    if (!isHidden) return;

    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const existed = !!meta;
    const previousContent = meta?.getAttribute('content') || '';

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (meta) {
        if (existed) {
          meta.setAttribute('content', previousContent);
        } else {
          meta.remove();
        }
      }
    };
  }, [isHidden]);

  if (loading) return null;
  if (isAdmin) return <>{children}</>;
  if (isHidden) return <NotFound />;
  return <>{children}</>;
};

export default ProtectedPage;
